import React from 'react';
import { Box, Paper, Typography, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatCurrency } from '../../utils/Currency';

const KPICard = ({ label, value, subtext, color, loading, showValues, isPercentage, isPositive }) => {
  const theme = useTheme();

  const displayValue = () => {
    if (loading) {
      return <Skeleton width={120} height={40} />;
    }
    if (!showValues) {
      return '--';
    }
    if (isPercentage) {
      const sign = isPositive ? '+' : '';
      return `${sign}${value?.toFixed(2)}%`;
    }
    return value;
  };

  const getValueColor = () => {
    if (isPercentage && showValues && value !== null) {
      return isPositive ? theme.palette.success.main : theme.palette.error.main;
    }
    return theme.palette.text.primary;
  };

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        borderLeft: `4px solid ${color}`,
        boxShadow: theme.shadows[2],
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: theme.palette.text.secondary,
          mb: 1,
          display: 'block',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: getValueColor(),
        }}
      >
        {displayValue()}
      </Typography>
      {subtext && (
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            mt: 0.5,
            display: 'block',
          }}
        >
          {loading ? <Skeleton width={80} /> : showValues ? subtext : ''}
        </Typography>
      )}
    </Paper>
  );
};

const KPICards = ({ data, currency, loading, showValues }) => {
  const theme = useTheme();

  const totalInvested = data?.totalInvested || 0;
  const currentValue = data?.currentValue || 0;
  const variation = totalInvested > 0 ? ((currentValue - totalInvested) / totalInvested) * 100 : 0;
  const profitLoss = currentValue - totalInvested;
  const isPositive = variation >= 0;

  const kpis = [
    {
      label: 'Total Investido',
      value: formatCurrency(currency, totalInvested),
      subtext: `Em ${data?.assetCount || 0} ativos`,
      color: theme.palette.info.main,
    },
    {
      label: 'Valor Atual',
      value: formatCurrency(currency, currentValue),
      subtext: 'Atualizado agora',
      color: theme.palette.warning.main,
    },
    {
      label: 'Rentabilidade',
      value: variation,
      subtext: showValues ? formatCurrency(currency, profitLoss) : '',
      color: isPositive ? theme.palette.success.main : theme.palette.error.main,
      isPercentage: true,
      isPositive: isPositive,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
        },
        gap: 2,
        mb: 3,
      }}
    >
      {kpis.map((kpi, index) => (
        <KPICard
          key={index}
          label={kpi.label}
          value={kpi.value}
          subtext={kpi.subtext}
          color={kpi.color}
          loading={loading}
          showValues={showValues}
          isPercentage={kpi.isPercentage}
          isPositive={kpi.isPositive}
        />
      ))}
    </Box>
  );
};

export default KPICards;
